'use client'

import React, { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import gsap from 'gsap'
import { useLanguage } from '../../providers/LanguageProvider'

const features = [
  {
    title: 'Self-storage solutions',
    fullTitle: 'Self-storage solutions for Personal and Business use',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/selfstoragesolutions.jpg', ///images/home/selfstoragesolutions.jpg
    alt: 'Aerial view of a desert shipping container yard with rows of containers and trucks, flanked by light structures and unpaved pathways.',
  },
  {
    title: 'Open yard storage',
    fullTitle: 'Open yard storage for Equipment and Oversized assets',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/openyard%20storage%20(2).jpg', ///images/home/openyard storage (2).jpg
    alt: 'Aerial view of a parking lot filled with various colored cars, with a truck unloading vehicles near shipping containers.',
  },
  {
    title: 'Container leasing & sales',
    fullTitle: 'Container leasing & sales including custom modifications',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/storage%20solutions.jpg', ///images/home/storage solutions.jpg
    alt: 'Aerial view of a busy shipping yard filled with colorful cargo containers and trucks, with storage facilities in the background.',
  },
  {
    title: 'Container depot services',
    fullTitle: 'Container depot services: tracking, repairs, inspections',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/openyard%20storage.jpg', ///images/home/openyard storage.jpg
    alt: 'Two workers in safety vests oversee a forklift unloading steel pipes on a vast, empty construction site. Trucks are in the background.',
  },
  {
    title: 'Value-added services',
    fullTitle: 'Value-added services: Inventory, Handling, Bonded Storage.',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/value%20added.jpg',
    alt: 'A truck with a branded trailer parked beside stacked shipping containers in a logistics yard under a hazy sky.',
  },
  {
    title: 'Dedicated Zones',
    fullTitle: 'Dedicated Zones: helium tanks, cold storage, vehicle storage, & more',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/dedicated%20zone.jpg',
    alt: 'Aerial view of a busy shipping yard filled with colorful stacked containers and trucks, with parked cars in the background.',
  },
]

const rtlFeatures = [
  {
    title: ' حلول التخزين الذاتي',
    fullTitle: ' حلول التخزين الذاتي للاستخدام الشخصي والعملي',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/selfstoragesolutions.jpg',
    alt: 'Aerial view of a desert shipping container yard with rows of containers and trucks, flanked by light structures and unpaved pathways.',
  },
  {
    title: ' تخزين في ساحة مفتوحة',
    fullTitle: 'تخزين في ساحة مفتوحة للمعدات والأصول ذات الأحجام الكبيرة',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/openyard%20storage%20(2).jpg',
    alt: 'Aerial view of a parking lot filled with various colored cars, with a truck unloading vehicles near shipping containers.',
  },
  {
    title: 'تأجير وبيع الحاويات',
    fullTitle: 'تأجير وبيع الحاويات، بما في ذلك التعديلات المخصصة',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/storage%20solutions.jpg',
    alt: 'Aerial view of a busy shipping yard filled with colorful cargo containers and trucks, with storage facilities in the background.',
  },
  {
    title: ' خدمات مستودع الحاويات',
    fullTitle: ' خدمات مستودع الحاويات: التتبع، الإصلاحات، الفحوصات',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/openyard%20storage.jpg',
    alt: 'Two workers in safety vests oversee a forklift unloading steel pipes on a vast, empty construction site. Trucks are in the background.',
  },
  {
    title: ' خدمات ذات قيمة مضافة',
    fullTitle: 'خدمات ذات قيمة مضافة: إدارة المخزون، التحميل/التفريغ، التخزين المضمون',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/value%20added.jpg',
   alt: 'A truck with a branded trailer parked beside stacked shipping containers in a logistics yard under a hazy sky.',
  },
  {
    title: ' مناطق مخصصة ',
    fullTitle: ' مناطق مخصصة لخزانات الهيليوم، والتبريد، وتخزين المركبات، وأكثر',
    image: 'https://d3lvumh1qhmw7p.cloudfront.net/dedicated%20zone.jpg',
   alt: 'Aerial view of a busy shipping yard filled with colorful stacked containers and trucks, with parked cars in the background.',
  },
]


type Locale = 'en' | 'ar'

const defaultImage = 'https://d3lvumh1qhmw7p.cloudfront.net/selfstoragesolutions.jpg'

const SmartStorage = () => {
  const [activeMobileTab, setActiveMobileTab] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const { language } = useLanguage()
  const isArabic = language === 'ar'

  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.why-heading', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 85%',
          once: true,
        },
      })

      gsap.from('.why-paragraph', {
        y: 30,
        opacity: 0,
        duration: 0.3,
        delay: 0.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
          once: true,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="w-full flex justify-center bg-black text-white pt-10 sm:pt-[40px] md:pt-0 pb-16 sm:pb-20 md:pb-[115px]">
      <div className="section-container flex flex-col gap-5">
        {/* <h2 className="why-heading text-3xl font-semibold text-center">Smart Storage. Seamless Logistics.</h2>
        <p className="why-paragraph text-gray-300 text-center max-w-3xl mx-auto px-4">
          At Container City®, we don’t just store your cargo—we manage your logistics lifecycle. Our state-of-the-art facility in Bu Jood, just 12 km from Hamad Port, is Qatar’s first fully legalized container storage hub, offering unmatched convenience and connectivity.
        </p> */}
        {/* Header */}
        {isArabic ? (
          <div className="sm:text-center flex flex-col gap-4 sm:gap-5 md:gap-8">
            <h2 className="text-white why-heading text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] font-[500]">
              تخزين ذكي. لوجستيات سلسة.
            </h2>
            <p className="text-gray-300 why-paragraph text-[14px] sm:text-[15px] md:text-[16px] xl:text-lg 2xl:text-lg mx-auto leading-relaxed px-3 sm:px-10 md:px-20 2xl:px-0">
              في Container City®، نحن لا نقوم فقط بتخزين بضائعك-بل ندير دورة حياة لوجستياتك. منشأتنا المتطورة
              في بو جُود، على بُعد 12 كم فقط من ميناء حمد، هي أول مركز تخزين حاويات مُرخص بالكامل في
              قطر، ويقدّم راحة واتصال لا مثيل لهما.
            </p>
          </div>
        ) : (
          <div className="sm:text-center flex flex-col gap-4 sm:gap-5 md:gap-8">
            <h2 className="text-white why-heading text-[24px] sm:text-[28px] md:text-[32px] xl:text-[38px] 2xl:text-[42px] font-[500]">
              Smart Storage. Seamless Logistics.
            </h2>
            <p className="text-gray-300 why-paragraph text-[15px] sm:text-[15px] md:text-[16px] xl:text-[16px] 2xl:text-lg text-left! sm:text-center leading-5">
              At Container City®, we don’t just store your cargo-we manage your logistics lifecycle Our
              state-of-the-art facility in Bu Jood, just 12 km <br className='hidden lg:block' /> from Hamad Port, is Qatar’s
              first fully legalized container storage hub, offering unmatched convenience and
              connectivity.
            </p>
          </div>
        )}

        {/* Mobile View */}
        <div className="flex flex-col gap-4 md:hidden">
          {(isArabic ? rtlFeatures : features).map((item, index) => (
            <div key={index} className="border-b border-gray-600">
              <button
                onClick={() => setActiveMobileTab(activeMobileTab === index ? null : index)}
                className="w-full flex justify-between items-center py-4 md:px-4 text-left font-medium text-lg text-white"
              >
                {item.title}
                <span className="text-[#EE3E25] text-2xl">{activeMobileTab === index ? '-' : '+'}</span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ${activeMobileTab === index ? 'max-h-[1000px] py-4 md:px-4 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <p className="text-gray-300 mb-3">{item.fullTitle}</p>
                <div className="w-full h-52 relative">
                 <Image    src={item.image} alt={item.alt} fill className="object-cover" />

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop View */}
        <div className="hidden md:flex flex-col md:flex-row gap-8 xl:gap-10 mt-6 sm:mt-8 md:mt-10 2xl:mt-10">
          <div className="w-full md:w-[56%] lg:w-[65%]">
            <p className="mb-4 text-[15px] sm:text-[17px] md:text-[19px] 2xl:text-[20px] font-light px-1 sm:px-0">
              {isArabic
                ? 'نخدم الجميع من الشركات الصغيرة والمتوسطة...'
                : 'We Serve Everyone From SMEs To Large Corporations, Providing:'}
            </p>
            <ul className="mt-5 sm:mt-6 md:mt-8">
              {(isArabic ? rtlFeatures : features).map((feature, index) => (
                <li key={index}>
                  <button
                    className="group w-full text-left h-[50px] lg:h-[82px] 2xl:h-[85px] font-light text-[15px] md:text-[16px] xl:text-[24px] 2xl:text-[28px] 2xl:hover:text-[30px] hover:font-normal group overflow-hidden relative"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <span className="absolute inset-0 bg-[#EE3E25] origin-center scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out z-0 group-hover:opacity-100 opacity-0" />
                    <div className={`relative z-10 border-gray-600 w-full h-full flex items-center ${index === features.length - 1 ? 'border-none' : 'border-b'}`}>
                      <span className="transition-colors group-hover:ml-2 duration-300 group-hover:text-white">
                        {hoveredIndex === index ? feature.fullTitle : feature.title}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="w-full md:w-[44%] lg:w-[35%] relative overflow-hidden h-[220px] sm:h-[280px] md:h-auto">
  <Image
    src={
      (hoveredIndex !== null
        ? (isArabic ? rtlFeatures : features)[hoveredIndex]?.image
        : defaultImage) || defaultImage
    }
    alt={
      (hoveredIndex !== null
        ? (isArabic ? rtlFeatures : features)[hoveredIndex]?.alt
        : 'Default storage facility image') || 'Default storage facility image'
    }
    fill
    className="object-cover transition-opacity duration-300"
  />
</div>

        </div>
      </div>
    </section>
  )
}

export default SmartStorage
